import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DefaultButton, Label, Separator, Stack, Text as UIText, TextField, Modal, Dropdown, IDropdownOption, IconButton, IContextualMenuProps, ChoiceGroup, IChoiceGroupOption, PrimaryButton, Icon, Spinner, DialogFooter, Dialog, DialogType, ITextFieldProps, Callout, IStackTokens, DirectionalHint, TeachingBubble } from "@fluentui/react";
import { 
    getPromptTemplateCategories, 
    getIsPublicTemplateCreatable, 
    PromptTemplateVar, 
    PromptTemplateVarInputType, 
    addPromptTemplate, 
    modifyPromptTemplate,
    PromptTemplateSchema, 
    getAvailableOwners,
    getAvailableGroups,
    getPromptTemplate,
    getPromptTemplateGroups, 
    PromptTemplate,
    updatePromptTemplateGroups, 
    getAvailableModels,
    ModelInfo,
    RAGMenuItem,
    RAGMenuList} from "../../api";
import { useCallback, useEffect, useRef, useState } from "react";
import { SlateEditor, ReflectToEditor, CustomText } from "./SlateEditor";
import { withReact } from "slate-react";
import { createEditor } from "slate";
import { LabelWithDescription } from "../../components/common/Label";
import { factories, getFactory, getUndroppableInputTypes } from "./VarElementFactory";
import { FileInfo, FilePicker } from "../../components/common/FilePicker";
import { MAX_FILE_COUNT } from "../../constants/constants";

interface EditVarsModalContentProps{
    onVarsChanged: (vars: PromptTemplateVar[]) => void
    dismissModal: () => void
    vars: PromptTemplateVar[]
}
const EditVarsModalContent = (props: EditVarsModalContentProps) => {
    type State = "List" | "Delete" | PromptTemplateVarInputType
    const [currentState, setCurrentState] = useState<State> ("List")
    const [focusVarId, setFocusVarId] = useState<string> ("")
    const focusElement = props.vars.filter((v) => v.id == focusVarId).at(0)
    const backToList = () => setCurrentState("List")

    const EditVarsModalContent_List = () => {
        const menuProps: IContextualMenuProps = {
            shouldFocusOnMount: true,
            items: factories.map((fac) => {
                return { 
                    key: fac.inputType,     
                    iconProps: { iconName: fac.iconName },     
                    text: fac.itemName, 
                    onClick: () => {
                        setFocusVarId("")
                        setCurrentState(fac.inputType) 
                    }
                }
            })
        }
        const PromptTemplateVarElement = (props: {var: PromptTemplateVar}) => {
            return (
                <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{childrenGap: "10px"}}>
                    <Stack horizontal verticalAlign="center" tokens={{childrenGap: "10px"}}>
                        <Icon iconName={getFactory(props.var.input_type)?.iconName} />
                        <UIText style={{fontSize: "13pt"}}>{props.var.name}</UIText>
                    </Stack>
                    <Stack horizontal verticalAlign="center" tokens={{childrenGap: "4px"}}>
                        <IconButton 
                            iconProps={{iconName: "Edit"}} 
                            onClick={() => {
                                setFocusVarId(props.var.id)
                                setCurrentState(props.var.input_type as State) 
                            }}
                        />
                        <IconButton 
                            iconProps={{iconName: "Delete"}}
                            onClick={() => {
                                setFocusVarId(props.var.id)
                                setCurrentState("Delete") 
                            }}
                        />
                    </Stack>
                </Stack>
            )
        }
        return (
            <>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" id="editvarsmodalcontent-header">
                    <h2>
                      質問項目一覧
                    </h2>
                    <IconButton
                      iconProps={{iconName: 'Cancel'}}
                      onClick={props.dismissModal}
                    />
                </Stack>
                <Stack id="editvarsmodalcontent-body" tokens={{childrenGap: "10px"}} style={{maxHeight: "70vh", minHeight: "70px", overflowY: "auto", marginBottom: "30px"}}>
                    <DefaultButton text="＋　質問項目の追加" menuProps={menuProps} style={{maxWidth: "200px", marginTop: "0px", marginBottom: "20px"}} />
                    {props.vars.map((v) => {
                        return (<div key={v.id} >
                            <PromptTemplateVarElement var={v}/>
                        </div>)
                    })}
                </Stack>
            </>
        )
    }
    const EditVarsModalContent_Delete = () => {
        return (
            <>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" id="editvarsmodalcontent-header">
                    <h2>
                      質問項目の削除
                    </h2>
                    <IconButton
                      iconProps={{iconName: 'Cancel'}}
                      onClick={props.dismissModal}
                    />
                </Stack>
                <Stack id="editvarsmodalcontent-body" tokens={{childrenGap: "10px"}} style={{maxHeight: "70vh", minHeight: "70px", overflowY: "auto"}}>
                    <UIText>この項目を削除しますがよろしいですか？<br />
                    プロンプトテキスト内からも削除されます。</UIText>
                </Stack>
                <Stack horizontal tokens={{childrenGap: "26px"}} style={{marginTop: "20px", marginBottom: "20px"}}>
                    <PrimaryButton text={"削除"} style={{width: "120px"}} onClick={() => {
                        props.onVarsChanged(props.vars.filter((v) => v.id !== focusElement?.id))
                        backToList()
                    }} />
                    <DefaultButton text="キャンセル" style={{width: "120px"}} onClick={backToList} />
                </Stack>
            </>
        )
    }
    const VarElement = getFactory(currentState)?.editVarsModalContent

    return (
    <Stack style={{marginLeft: "20px", marginRight: "20px", width: currentState === "Delete" ? "30vw" : "40vw", minWidth: "500px"}}>
        { currentState === "List" && <EditVarsModalContent_List /> }
        { currentState === "Delete" && <EditVarsModalContent_Delete /> }
        {
            VarElement && <VarElement 
                focusElement={focusElement}
                vars={props.vars}
                onVarsChanged={props.onVarsChanged}
                backToList={backToList}
            />
        }
    </Stack>)
}

const TemplateVarElement = (props: {var: PromptTemplateVar}) => {
    const v = props.var
    return (
        <span 
            draggable 
            contentEditable={false}
            onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", JSON.stringify({text: v.name, var_id: v.id}));
            }}
            style={{
                backgroundColor: "#ffffff", 
                border: "solid lightblue 2px",
                borderRadius: "8px", 
                padding: "8px",
                cursor: "pointer",
                display: "inline-block", // inline-blockに変更
                margin: "2px 2px", // 左右に少しマージンを追加
                maxWidth: "200px", 
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
            }}>
            <UIText style={{userSelect: "none"}}>{v.name}</UIText>
        </span>
    )
}

export const is_model_usable = (model_info: ModelInfo, tmp_tools: string[]) => {
    return tmp_tools.every((tmp_tool) => model_info.tools.map(vv => vv.name).includes(tmp_tool))
}

export const is_rag_accessible = async (rag_ids: number[], rag_menu_list: RAGMenuItem[] | undefined) => {
    const available_rag = rag_menu_list ?? await RAGMenuList()
    if (available_rag === null) {
        return false
    }
    const available_rag_ids = available_rag.map((v) => Number(v.id))
    return rag_ids.every((rag_id) => available_rag_ids.includes(rag_id))
}

const all_vars_used_in_request = (vars: PromptTemplateVar[], request: string) => {
    const undroppableInputTypes = getUndroppableInputTypes()
    const filtered = vars.filter(v => !undroppableInputTypes.includes(v.input_type))
    for (const v of filtered) {
        if (!request.includes(`{{${v.id}}}`)) return false
    }
    return true
}

const all_vars_unused_in_request_despite_exists = (vars: PromptTemplateVar[], request: string) => {
    const undroppableInputTypes = getUndroppableInputTypes()
    const filtered = vars.filter(v => !undroppableInputTypes.includes(v.input_type))
    if (filtered.length == 0) return false
    for (const v of filtered) {
        if (request.includes(`{{${v.id}}}`)) return false
    }
    return true
}

interface NewFileInfo {
    file: File;
    id: string
}

export default function EditPromptTemplate(){
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const id_num = id ? Number(id) : undefined
    const category_id = searchParams.get('category_id');
    const category_id_num = category_id ? Number(category_id) : undefined
    const copy_from = searchParams.get('copy_from');
    const copy_from_num = copy_from ? Number(copy_from) : undefined

    const [editor] = useState(() => withReact(createEditor()))
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)

    const formRef = useRef<HTMLFormElement>(null);
    const originalTemplate = useRef<PromptTemplate|null>(null)
    const [category, setCategory] = useState(category_id_num ? category_id_num : -1)
    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    // 利用可能なモデル一覧
    const [availableModels, setAvailableModels] = useState<{key: string, info: ModelInfo}[]>([])
    // 実際に選択肢として表示されるモデルたち
    const [modelOptions, setModelOptions] = useState<IDropdownOption[]>([])
    const [tmpType, setTmpType] = useState("private")
    const [ownerId, setOwnerId] = useState("")
    const [availableOwners, setAvailableOwners] = useState<string[]>([])
    const [availableRags, setAvailableRags] = useState<IDropdownOption[]>([])
    const [ragIds, setRagIds] = useState<number[]>([])
    const [groups, setGroups] = useState<number[]>([])
    const [availableGroups, setAvailableGroups] = useState<IDropdownOption[]>([])
    const [tools, setTools] = useState<string[]>([])
    const [modelType, setModelType] = useState("")
    const [currentFiles, setCurrentFiles] = useState<FileInfo[]>([]);
    const [newFiles, setNewFiles] = useState<NewFileInfo[]>([]);
    const [reqPrompt, setReqPrompt] = useState<string>("")
    const [sysPrompt, setSysPrompt] = useState("")
    const [vars, setVars] = useState<PromptTemplateVar[]>([])
    const [categories, setCategories] = useState<IDropdownOption[]>([])
    const [isPublicTmpCreatable, setIsPublicTmpCreatable] = useState(false)

    // モーダル制御用
    const [isEditVarsModalOpen, setIsEditVarsModalOpen] = useState(false)
    const [isMessageModalOpen, setIsMessageModalOpen] = useState(false)
    const [modalMessageTitle, setModalMessageTitle] = useState("")
    const [modalMessage, setModalMessage] = useState("")
    const [modalMessageWarning, setModalMessageWarning] = useState("")

    // 質問項目がリクエストに含まれない時の確認用
    const [isUnsetWarningModalOpen, setIsUnsetWarningModalOpen] = useState(false)

    // リクエストにドロップできない変数タイプ
    const undroppableInputTypes = getUndroppableInputTypes()
    const droppableVars = vars.filter((v) => !undroppableInputTypes.includes(v.input_type))

    const handleReqPromptChange = useCallback((prompt: string) => {
        setReqPrompt(prompt);
    }, []);

    const handleSubmit = () => {
        setTimeout(() => {
          formRef.current?.requestSubmit()
        }, 0)
    }

    // ファイルたち
    // ファイルリストは、現在のテンプレートのファイル→新たに追加されるファイルの順
    const attachedFiles: FileInfo[] = [
        ...currentFiles, 
        ...newFiles.map((info) => ({name: info.file.name, id: info.id}))
    ]
    const onRemoveFile = (fileId: string) => {
        setCurrentFiles((v) => v.filter(vv => vv.id !== fileId));
        setNewFiles((v) => v.filter(vv => vv.id !== fileId));
    }
    const onFilePicked = (files: FileList | null) => {
        if (files !== null) {
            const fileList = [...files];
            setNewFiles([...newFiles, ...fileList.map(f => ({file: f, id: crypto.randomUUID()}))])
        }
    }
    
    const dataload = async () => {
        setIsLoading(true)
        // カテゴリー
        const cates = await getPromptTemplateCategories()
        setCategories(cates.map((v) => ({key: v.id, text: v.name, disabled: Boolean(category_id_num) && category_id_num !== v.id})))
        
        // パブリックテンプレートが作成可能か？
        const creatable = await getIsPublicTemplateCreatable()
        setIsPublicTmpCreatable(creatable)
        // 利用可能なモデルたち
        const models = await getAvailableModels()
        if(models){
            setAvailableModels([...models.entries()].map(([k, v]) => ({key: k, info: v})))
        }
        
        // 設定可能な所有者たち
        const users = await getAvailableOwners()
        setAvailableOwners(users)
        
        // 設定可能なグループたち
        const groups = (await getAvailableGroups()).sort((a, b) => a.cd - b.cd)
        setAvailableGroups(groups.map((v) => ({key: v.cd, text: `${String(v.cd)}_${v.name}`})))

        // 利用可能なRAGカテゴリたち
        const rags = await RAGMenuList() ?? []
        setAvailableRags(rags.map((v) => ({key: Number(v.id), text: v.name})))
        const rag_ids = rags.map((v) => Number(v.id))

        setNewFiles([]);

        // 新規作成
        if (!id_num) {
            // コピー元が存在
            if (copy_from_num) {
                const template = await getPromptTemplate(copy_from_num)
                if (!template) {
                    setModalMessageTitle("エラー")
                    setModalMessage("コピー元テンプレートの取得に失敗しました\nログアウトされているか、IDが不正か、権限が不足している可能性があります")
                    setIsMessageModalOpen(true)
                }
                else {
                    // 自分用にしておく
                    setOwnerId(users.at(0) ?? "")
                    setTmpType("private")
                    setName(`${template.name}（コピー）`)
                    setDescription(template.description)
                    setCategory(template.category_id)
                    
                    const schema = template.schema
                    setSysPrompt(schema.system_prompt)
                    setVars(schema.vars)
                    setReqPrompt(schema.request_prompt)
                    setTools(schema.tools)
                    setRagIds(schema.rag_ids.filter((v) => rag_ids.includes(v)))
                    setCurrentFiles(schema.files.map(f => ({name: f.file_name, blobFileName: f.file_name_in_blob, id: crypto.randomUUID() })))
                    if (schema.chat_model){
                        setModelType(schema.chat_model ?? "")
                    }
                    ReflectToEditor(schema.request_prompt, editor, schema.vars)
                }
            }
            // コピー元が存在しない
            else {
                setTmpType("private")
                setOwnerId(users.at(0) ?? "")
                setCategory(0 < category ? category : (cates.at(0)?.id ?? -1))
            }
        }
        // 修正
        else {
            const template = await getPromptTemplate(id_num)
            if (!template) {
                setModalMessageTitle("エラー")
                setModalMessage("テンプレートの取得に失敗しました\nログアウトされているか、IDが不正か、権限が不足している可能性があります")
                setIsMessageModalOpen(true)
            }
            else if (!creatable && !template.owner_id) {
                setModalMessageTitle("エラー")
                setModalMessage("パブリックテンプレートを編集する権限がありません")
                setIsMessageModalOpen(true)
            }
            else {
                originalTemplate.current = template
                setOwnerId(template.owner_id ?? users.at(0) ?? "")
                // プライベートテンプレート
                if (template.owner_id) {
                    setOwnerId(template.owner_id)
                    setTmpType("private")
                }
                // パブリックテンプレート
                else {
                    setOwnerId(users.at(0) ?? "")
                    setTmpType("public")
                    const g = await getPromptTemplateGroups(id_num)
                    setGroups(g)
                }
                setName(template.name)
                setDescription(template.description)
                setCategory(template.category_id)

                const new_schema = template.schema
                setSysPrompt(new_schema.system_prompt)
                setVars(new_schema.vars)
                setReqPrompt(new_schema.request_prompt)
                setTools(new_schema.tools)
                setRagIds(new_schema.rag_ids.filter((v) => rag_ids.includes(v)))
                setCurrentFiles(new_schema.files.map(f => ({name: f.file_name, blobFileName: f.file_name_in_blob, id: crypto.randomUUID() })))
                if (new_schema.chat_model){
                    setModelType(new_schema.chat_model ?? "")
                }
                ReflectToEditor(new_schema.request_prompt, editor, new_schema.vars)
            }
        }
        setIsLoading(false)
    }

    // データフェッチ
    useEffect(() => {
        dataload()
    }, [id_num])

    const model_info = availableModels.find((v) => v.key == modelType)
    const all_tools = Array.from(
        new Map(availableModels.flatMap(v => v.info.tools).map((tool) => [tool.name, tool])).values()
    )

    const promptTemplateTypes: IDropdownOption[] = [
        { key: 'private', text: '個人用' },
        { key: 'public', text: '組織用', disabled: !isPublicTmpCreatable },
    ];
    const chatTools: IDropdownOption[] = (model_info?.info.tools ?? all_tools)
    .filter(v => 
        v.arg_schema === null // パラメータを持つツールは封印
    )    
    .map(v => ({
        key: v.name, text: v.display_name
    }))

    // ツールまたはモデル集合が変更されたら、選択可能なモデルを修正
    useEffect(() => {
        const options = [
            { key: '', text: '指定しない', disabled: false }, 
            ...availableModels.map((v) => {
                return {key: v.key, text: v.info.name, disabled: !is_model_usable(v.info, tools) }
            })
        ]
        setModelOptions(options)
    }, [tools, availableModels])

    // モデルが変更されたらツールをモデルに合わせて更新
    useEffect(() => {
        if (model_info === undefined) return
        setTools(tools.filter((v) => model_info.info.tools.map(vv => vv.name).includes(v)))
    }, [model_info])

    return(<>
        <form ref={formRef} onSubmit={async (e) => {
            if (!e.currentTarget.checkValidity()) {
              return
            }
            e.preventDefault()
            try{
                setIsLoading(true)
                
                // ファイル個数チェック
                if (MAX_FILE_COUNT < currentFiles.length + newFiles.length) {
                    setModalMessageTitle("エラー")
                    setModalMessage(`${MAX_FILE_COUNT}個より多いファイルはアップロードできません。`)
                    setIsMessageModalOpen(true)
                    return;
                }

                const schema: PromptTemplateSchema = {
                    system_prompt: sysPrompt,
                    request_prompt: reqPrompt,
                    tools: tools,
                    rag_ids: ragIds,
                    chat_model: modelType,
                    vars: vars,
                    files: currentFiles.map(f => ({file_name: f.name, file_name_in_blob: f.blobFileName ?? ""}))
                }
                // 新規作成
                if (!id_num) {
                    const is_public = tmpType === "public"
                    let template_id =  await addPromptTemplate(is_public ? null : ownerId, name, description, category, schema, newFiles.map(f => f.file))
                    // パブリックテンプレートを作成する場合はグループも追加する
                    if (template_id && is_public && groups.length != 0) {
                        const success = await updatePromptTemplateGroups(template_id, groups)
                        if (!success) template_id = undefined
                    }
                    if (template_id)
                        navigate(`/edit_prompt_template/${template_id}`)
                    setModalMessageTitle(template_id ? "完了" : "エラー")
                    setModalMessage(template_id ? "正常に作成されました\n変更を反映するには、元画面の一覧表示を再読み込みしてください" : "作成に失敗しました\nログアウトされているか、権限が足りない可能性があります")
                    if (!all_vars_used_in_request(schema.vars, schema.request_prompt)) {
                        setModalMessageWarning("リクエストに含まれない質問項目が存在します\nリクエストに存在しない質問項目は使用されません")
                    }
                    else {
                        setModalMessageWarning("")
                    }
                    setIsMessageModalOpen(true)
                }
                // 修正
                else if(originalTemplate.current) {
                    const is_public = tmpType === "public"
                    const prev_is_public = !Boolean(originalTemplate.current.owner_id)
                    const success =  await modifyPromptTemplate(
                        originalTemplate.current.id, originalTemplate.current.owner_id,
                        name, description, category, schema, originalTemplate.current.owner_id !== ownerId || (is_public != prev_is_public), is_public ? null : ownerId,
                        newFiles.map(f => f.file)
                    )
                    let success2 = true
                    if (isPublicTmpCreatable) {
                        // プライベートテンプレートならグループ削除、パブリックテンプレートなら選択状況のように更新
                        success2 = await updatePromptTemplateGroups(originalTemplate.current.id, is_public ? groups : [])
                    }
                    dataload()
                    setModalMessageTitle((success && success2) ? "完了" : "エラー")
                    setModalMessage((success && success2) ? "正常に保存されました\n変更を反映するには、元画面の一覧表示を再読み込みしてください" : "保存に失敗しました\nログアウトされているか、権限が足りない可能性があります")
                    if (!all_vars_used_in_request(schema.vars, schema.request_prompt)) {
                        setModalMessageWarning("いくつかの質問項目がリクエストに含まれていません\nリクエストに含まれていない質問項目は使用されません")
                    }
                    else {
                        setModalMessageWarning("")
                    }
                    setIsMessageModalOpen(true)
                }
            }
            finally{
                setIsLoading(false)
            }
        }}>
        <div style={{paddingTop: "20px", paddingBottom: "50px"}}>
            <Stack horizontalAlign="center" id="header" style={{marginBottom: "20px"}} horizontal>
                {isLoading ? <Spinner /> : <></>}
                <h2 style={{fontSize: "18pt"}}>{id_num ? "プロンプトテンプレート修正" : "プロンプトテンプレート新規作成"}</h2>
            </Stack>
            <Stack horizontalAlign="center" id="body" tokens={{childrenGap: "26px"}}>
                <UIText style={{color: "red", fontWeight: "bold"}}>* は入力必須項目です</UIText>
                <Stack tokens={{childrenGap: "26px", maxWidth: "502px"}}>
                    <Dropdown
                      placeholder="カテゴリを選択"
                      label="テンプレートカテゴリ"
                      options={categories}
                      selectedKey={category}
                      onChange={(_, option) => setCategory(Number(option?.key))}
                      required
                    />
                    <TextField 
                        value={name} 
                        onChange={(e) => setName(e.currentTarget.value)} 
                        label="テンプレート名" 
                        style={{minWidth: "500px"}} 
                        maxLength={20}
                        required 
                    />
                    <TextField 
                        value={description} 
                        onChange={(e) => setDescription(e.currentTarget.value)} 
                        label="テンプレート概要" 
                        style={{minWidth: "500px"}} 
                        maxLength={30}
                    />
                    <Dropdown
                      placeholder="種別を選択"
                      label="テンプレート種別"
                      options={promptTemplateTypes}
                      selectedKey={tmpType}
                      onChange={(_, option) => setTmpType(String(option?.key))}
                      required
                    />
                    { // プライベートテンプレートなら、所有者を選択する必要がある
                    tmpType == "private" ? <Dropdown
                      placeholder="所有者を選択"
                      label="テンプレート所有者"
                      options={availableOwners.map((v) => ({ key: v, text: v }))}
                      selectedKey={ownerId}
                      onChange={(_, option) => setOwnerId(String(option?.key))}
                      required
                    /> : <></>}
                    { // パブリックテンプレートなら、グループを選択する必要がある
                    tmpType == "public" ? <Dropdown
                      style={{width: "502px"}}
                      placeholder="グループを選択"
                      label="テンプレート利用可能グループ (指定しない場合は全グループで利用可能)"
                      multiSelect
                      options={availableGroups}
                      selectedKeys={groups}
                      onChange={(_, option) => {
                        if (option) {
                          if (option.selected) {
                            // アイテムが選択された場合
                            setGroups(prev => [...prev, option.key as number])
                          } else {
                            // アイテムが選択解除された場合
                            setGroups(prev => prev.filter(key => key !== option.key))
                          }
                        }
                      }}
                    /> : <></>}
                    <Dropdown
                      label="使用モデル"
                      options={modelOptions}
                      selectedKey={modelType}
                      onChange={(_, option) => setModelType(String(option?.key))}
                      required
                    />
                    <Stack horizontal tokens={{childrenGap: "20px"}}>
                        <Dropdown
                          label="チャットオプション"
                          placeholder="チャットオプションを選択"
                          multiSelect
                          options={chatTools}
                          selectedKeys={tools}
                          onChange={(_, option) => {
                          if (option) {
                            if (option.selected) {
                              // アイテムが選択された場合
                              setTools(prev => [...prev, String(option.key)])
                            } else {
                              // アイテムが選択解除された場合
                              setTools(prev => prev.filter(key => key !== option.key))
                            }
                           }
                          }}
                          style={{width: "240px"}}
                        />
                        <Dropdown
                          label="RAGカテゴリ"
                          placeholder="RAGカテゴリを選択"
                          multiSelect
                          options={availableRags}
                          selectedKeys={ragIds}
                          onChange={(_, option) => {
                          if (option) {
                            if (option.selected) {
                              // アイテムが選択された場合
                              setRagIds(prev => [...prev, option.key as number])
                            } else {
                              // アイテムが選択解除された場合
                              setRagIds(prev => prev.filter(key => key !== option.key))
                            }
                           }
                          }}
                          style={{width: "240px"}}
                        />
                    </Stack>
                    <FilePicker 
                        label="ファイル" 
                        listedFiles={attachedFiles} 
                        onFilePicked={onFilePicked} 
                        onRemoveFile={onRemoveFile} 
                    />
                </Stack>
                <Separator styles={{
                  root: {
                      width: '100%',
                      position: 'relative',
                      '::before': {
                        backgroundColor: '#d6d6d6',
                      },
                    },
                }}/>
                <Stack horizontalAlign="center" id="body2" tokens={{childrenGap: "26px"}}>
                    <Stack style={{backgroundColor: "#e8e8e8", width: "100%", minHeight: "100px", borderRadius: "10px", padding: "10px"}}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                            <Label>質問項目</Label>
                            <DefaultButton 
                                text="質問項目の追加・編集"
                                onClick={() => {setIsEditVarsModalOpen(true)}}
                            />
                        </Stack>
                        <Label disabled>
                            {droppableVars.length === 0 ? 
                            "質問項目を作成し、プロンプトに挿入することができます" 
                            : "「リクエスト」エリアにドラッグ＆ドロップしてください"}
                        </Label>
                        <Stack horizontal verticalAlign="center" style={{marginTop: "4px", width: "482px"}} tokens={{childrenGap: "10px"}} wrap>
                            {droppableVars.map((v) => <TemplateVarElement key={v.id} var={v}/>)}
                        </Stack>
                    </Stack>
                    { /* リクエスト編集用リッチエディタ */}
                    <LabelWithDescription label_description="AIに対して、具体的な質問や要求を行うプロンプトです" directional_hint={DirectionalHint.bottomLeftEdge}>
                        <Label>リクエスト *</Label>
                    </LabelWithDescription>
                    <SlateEditor 
                        editor={editor}
                        vars={vars}
                        onReqPromptChange={handleReqPromptChange}
                    />
                    <TextField 
                        onRenderLabel={(props: ITextFieldProps | undefined, defaultRender) => props ? (
                            <LabelWithDescription label_description="AIに対して、役割やふるまいを指定するプロンプトです" directional_hint={DirectionalHint.bottomLeftEdge}>
                                {defaultRender && defaultRender(props)}
                            </LabelWithDescription>
                        ) : null}
                        value={sysPrompt}
                        onChange={(e) => {
                            setSysPrompt(e.currentTarget.value)
                        }}
                        multiline rows={5} 
                        label="システムプロンプト" 
                        style={{minWidth: "500px", minHeight: "300px"}} 
                        onDrop={(e) => {
                            const text = e.dataTransfer.getData("text/plain")
                            const id_dict: CustomText = JSON.parse(text)
                            if (id_dict.text) {
                                e.preventDefault()
                            }
                        }}
                        placeholder={`[例文]
#命令
あなたは文面作成のプロです。
以下の[#条件]と[#入力]に基づいて、メール送信用の文面を作成してください。

#条件
・[#入力]に基づいてメールの文章を作成すること
・簡潔な文章とすること
・フォーマルな文章表現にすること
・専門用語は使わないこと

#出力形式
・項目ごとに改行や段落を付けること
・下記の形式で記載すること
■件名 (件名を記載)
■本文 (本文を記載)`}
                    />
                </Stack>
                <Stack horizontal tokens={{childrenGap: "30px"}}>
                    <PrimaryButton type="submit" disabled={isLoading} onClick={(e) => {
                        e.preventDefault()
                        if(all_vars_unused_in_request_despite_exists(vars, reqPrompt)) {
                            if (!formRef.current?.checkValidity()) {
                              // submit側で弾いてもらう
                              handleSubmit()
                              return
                            }
                            setIsUnsetWarningModalOpen(true)
                        }
                        else {
                            handleSubmit()
                        }
                    }}>
                        {isLoading && <Spinner style={{marginRight: "6px"}} />}
                        {Boolean(id_num) ? "保存" : "追加"}
                    </PrimaryButton>
                    <DefaultButton disabled={isLoading} onClick={() => window.close()}>
                        閉じる
                    </DefaultButton>
                </Stack>
            </Stack>
        </div>
        </form>
        <Modal
            isOpen={isEditVarsModalOpen}
            isBlocking={true}
        >
            <EditVarsModalContent 
                onVarsChanged={(v) => setVars(new Array(...v))} 
                dismissModal={()=>setIsEditVarsModalOpen(false)}
                vars={vars}
            />
        </Modal>
        {
            // 各種メッセージ用ダイアログ
        }
        <Dialog
            hidden={!isMessageModalOpen}
            onDismiss={setIsMessageModalOpen.bind(null, false)}
            dialogContentProps={{
                type: DialogType.close,
                title: modalMessageTitle,
                closeButtonAriaLabel: 'Close',
            }}
            modalProps={{
                titleAriaId: 'labelId',
                subtitleAriaId: 'subTextId',
                isBlocking: false,
                styles: { main: { width: 450 } },
            }}
        >
            <div style={{ padding: '10px', whiteSpace: 'pre-line' }}>
                {modalMessage}
            </div>
            <div style={{ padding: '10px', whiteSpace: 'pre-line', color: "red", fontWeight: "bold"}}>
                {modalMessageWarning}
            </div>
            <DialogFooter>
            <PrimaryButton onClick={setIsMessageModalOpen.bind(null, false)} text="OK" />
            </DialogFooter>
        </Dialog>
        {
            // 質問項目がリクエストに含まれていない警告用ダイアログ
        }
        <Dialog
            hidden={!isUnsetWarningModalOpen}
            onDismiss={setIsUnsetWarningModalOpen.bind(null, false)}
            dialogContentProps={{
                type: DialogType.close,
                title: "警告",
                closeButtonAriaLabel: 'Close',
            }}
            modalProps={{
                titleAriaId: 'labelId',
                subtitleAriaId: 'subTextId',
                isBlocking: false,
                styles: { main: { minWidth: 450 } },
            }}
        >
            <div style={{ padding: '10px', whiteSpace: 'pre-line', color: "red", fontWeight: "bold"}}>
                {"質問項目がリクエストに含まれていません\n使用するには、質問項目をリクエストにドラッグ＆ドロップしてください\n\nリクエストに含まれない質問項目は使用されません、続行しますか？"}
            </div>
            <DialogFooter>
                <PrimaryButton onClick={() => {
                    setIsUnsetWarningModalOpen(false)
                    handleSubmit()
                }} text="OK" />
                <DefaultButton onClick={setIsUnsetWarningModalOpen.bind(null, false)} text="キャンセル" />
            </DialogFooter>
        </Dialog>
    </>)
}